import { Link } from "react-router-dom";

import { PageContainer } from "@/components/core/page-container";
import { buttonVariants } from "@/components/ui/button";
import { ResponsiveImage } from "@/components/ui/responsive-image";
import { getOptimizedImageAsset } from "@/lib/image-assets";
import type { HeroSection as HeroSectionContent } from "@/types/site";

interface HeroSectionProps {
  content: HeroSectionContent;
}

const heroBackgroundImage = getOptimizedImageAsset(
  "/Forest-Photos/Forest-1.png",
);

export function HeroSection({ content }: HeroSectionProps): JSX.Element {
  return (
    <section className="relative flex min-h-[calc(100svh-4.5rem)] items-center justify-center overflow-hidden pb-16 pt-20 text-center sm:pb-20 sm:pt-24 md:min-h-[800px]">
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 overflow-hidden bg-[linear-gradient(180deg,hsl(148_24%_14%)_0%,hsl(150_24%_10%)_46%,hsl(30_22%_8%)_100%)]"
        data-testid="hero-ornament"
      >
        {heroBackgroundImage ? (
          <ResponsiveImage
            alt=""
            aria-hidden="true"
            containerClassName="absolute inset-0"
            fetchPriority="high"
            height={heroBackgroundImage.height}
            imgClassName="hero-backdrop-image absolute inset-0 h-full w-full object-cover"
            loading="eager"
            sizes="100vw"
            sources={heroBackgroundImage.sources}
            src={heroBackgroundImage.src}
            width={heroBackgroundImage.width}
          />
        ) : null}
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(7,14,10,0.2)_0%,rgba(7,10,8,0.46)_58%,rgba(7,8,8,0.78)_100%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,hsl(var(--hero-glow)/0.28),transparent_34%),radial-gradient(circle_at_20%_78%,hsl(var(--accent)/0.16),transparent_28%),radial-gradient(circle_at_80%_18%,rgba(255,255,255,0.12),transparent_24%)]" />
        <div className="hero-rotation absolute left-1/2 top-1/2 h-[135vmax] w-[135vmax] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[conic-gradient(from_0deg,transparent_0deg,rgba(215,192,150,0.03)_48deg,transparent_104deg,rgba(255,255,255,0.05)_160deg,transparent_212deg,rgba(215,192,150,0.04)_272deg,transparent_360deg)]" />
        <div className="hero-veil absolute inset-x-[12%] top-[14%] h-[48%] rounded-full border border-white/10 bg-white/[0.04] blur-2xl sm:inset-x-[14%] sm:top-[12%] sm:h-[54%]" />
        <div className="hero-veil absolute inset-x-[24%] bottom-[-14%] h-[34%] rounded-full border border-white/8 bg-[radial-gradient(circle,hsl(var(--accent)/0.16),transparent_68%)] blur-2xl [animation-delay:-3.6s] sm:inset-x-[28%] sm:h-[42%]" />
        <div className="hero-sheen absolute left-1/2 top-[-14%] h-[118%] w-20 -translate-x-1/2 rotate-[16deg] bg-[linear-gradient(180deg,transparent_0%,rgba(232,214,181,0.16)_46%,transparent_100%)] opacity-70 blur-xl [animation-delay:-3s] sm:h-[132%] sm:w-28" />
        <div className="absolute inset-x-0 bottom-0 h-[42%] bg-[linear-gradient(180deg,transparent,rgba(0,0,0,0.52)_74%,rgba(0,0,0,0.82))]" />
      </div>

      <PageContainer className="relative z-10 flex max-w-4xl flex-col items-center px-4 sm:px-6 md:px-8">
        {content.eyebrow ? (
          <p className="mb-5 text-xs font-medium uppercase tracking-[0.28em] text-white drop-shadow-md animate-fade-up sm:mb-6 sm:text-sm sm:tracking-[0.32em]">
            {content.eyebrow}
          </p>
        ) : null}

        <h1
          className="mb-5 whitespace-pre-line font-display text-[2.6rem] font-normal leading-[1.02] tracking-[-0.03em] text-balance text-white drop-shadow-lg animate-fade-up sm:mb-6 sm:text-[3.35rem] sm:leading-[1.06] md:text-[5rem] md:tracking-[-0.02em] lg:text-[64px]"
          style={{ animationDelay: "150ms" }}
        >
          {content.title}
        </h1>

        <p
          className="mb-8 max-w-2xl text-base font-light leading-7 text-white/90 drop-shadow-md animate-fade-up sm:mb-10 sm:text-lg sm:leading-relaxed md:text-[18px]"
          style={{ animationDelay: "300ms" }}
        >
          {content.description}
        </p>

        <div
          className="flex w-full flex-col gap-4 animate-fade-up sm:w-auto sm:flex-row sm:justify-center"
          style={{ animationDelay: "450ms" }}
        >
          <Link
            className={buttonVariants({
              className:
                "w-full border border-black bg-black px-6 py-4 text-center uppercase tracking-[0.1em] text-white transition-all duration-500 hover:scale-[1.02] hover:bg-black/80 sm:w-auto sm:px-10 sm:py-6",
            })}
            to={content.ctaHref}
          >
            {content.ctaLabel}
          </Link>
        </div>
      </PageContainer>
    </section>
  );
}
